/**
 * SearchInput Component
 *
 * Reusable search input with debouncing, clear button, and loading state.
 *
 * @example
 * // Basic usage
 * <SearchInput
 *   value={search}
 *   onChange={setSearch}
 *   placeholder="Search clients..."
 * />
 *
 * @example
 * // With debouncing
 * <SearchInput
 *   value={search}
 *   onChange={handleSearch}
 *   debounceMs={300}
 *   isLoading={isSearching}
 * />
 */

import { useState, useEffect, useCallback, useRef, forwardRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * SearchInput props
 */
export interface SearchInputProps {
  /** Current search value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Debounce delay in ms (0 = no debounce) */
  debounceMs?: number;
  /** Whether search is in progress */
  isLoading?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Additional className */
  className?: string;
  /** Container className */
  containerClassName?: string;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to auto-focus */
  autoFocus?: boolean;
  /** Callback when Enter is pressed */
  onSubmit?: () => void;
  /** Callback when Escape is pressed */
  onEscape?: () => void;
  /** Show clear button */
  showClear?: boolean;
  /** Minimum characters before triggering search */
  minChars?: number;
}

/**
 * Search input with debouncing and clear functionality
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      placeholder = 'Search...',
      debounceMs = 0,
      isLoading = false,
      disabled = false,
      className,
      containerClassName,
      size = 'md',
      autoFocus = false,
      onSubmit,
      onEscape,
      showClear = true,
      minChars = 0,
    },
    ref
  ) => {
    // Internal state for immediate UI updates
    const [internalValue, setInternalValue] = useState(value);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Sync internal state with external value
    useEffect(() => {
      setInternalValue(value);
    }, [value]);

    // Handle input change with debouncing
    const handleChange = useCallback(
      (newValue: string) => {
        setInternalValue(newValue);

        // Clear existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Check minimum characters
        if (minChars > 0 && newValue.length < minChars && newValue.length > 0) {
          return;
        }

        // Debounce or immediate
        if (debounceMs > 0) {
          timeoutRef.current = setTimeout(() => {
            onChange(newValue);
          }, debounceMs);
        } else {
          onChange(newValue);
        }
      },
      [onChange, debounceMs, minChars]
    );

    // Clear on unmount
    useEffect(() => {
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, []);

    // Handle clear
    const handleClear = useCallback(() => {
      setInternalValue('');
      onChange('');
    }, [onChange]);

    // Handle keyboard events
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && onSubmit) {
          e.preventDefault();
          onSubmit();
        }
        if (e.key === 'Escape') {
          if (internalValue && showClear) {
            handleClear();
          } else if (onEscape) {
            onEscape();
          }
        }
      },
      [onSubmit, onEscape, internalValue, showClear, handleClear]
    );

    // Size classes
    const sizeClasses = {
      sm: 'h-8 text-sm pl-8 pr-8',
      md: 'h-10 text-base pl-10 pr-10',
      lg: 'h-12 text-lg pl-12 pr-12',
    };

    const iconSizes = {
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };

    const iconPositions = {
      sm: 'left-2.5',
      md: 'left-3',
      lg: 'left-4',
    };

    const clearPositions = {
      sm: 'right-1',
      md: 'right-1',
      lg: 'right-2',
    };

    const hasValue = internalValue.length > 0;

    return (
      <div className={cn('relative', containerClassName)}>
        {/* Search icon */}
        <Search
          className={cn(
            'absolute top-1/2 -translate-y-1/2 text-muted-foreground',
            iconSizes[size],
            iconPositions[size]
          )}
        />

        {/* Input */}
        <Input
          ref={ref}
          type="text"
          value={internalValue}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          className={cn(sizeClasses[size], className)}
        />

        {/* Right side: loading or clear */}
        <div
          className={cn(
            'absolute top-1/2 -translate-y-1/2 flex items-center gap-1',
            clearPositions[size]
          )}
        >
          {isLoading && (
            <Loader2
              className={cn('animate-spin text-muted-foreground', iconSizes[size])}
            />
          )}
          {showClear && hasValue && !isLoading && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                'h-6 w-6 hover:bg-muted',
                size === 'sm' && 'h-5 w-5'
              )}
              onClick={handleClear}
            >
              <X className={iconSizes[size]} />
              <span className="sr-only">Clear search</span>
            </Button>
          )}
        </div>
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';

/**
 * Compact search button that expands to input
 */
export interface ExpandableSearchProps extends Omit<SearchInputProps, 'autoFocus'> {
  /** Whether the search is expanded */
  isExpanded?: boolean;
  /** Callback to toggle expansion */
  onExpandedChange?: (expanded: boolean) => void;
}

/**
 * Search that starts as a button and expands to input
 *
 * @example
 * const [expanded, setExpanded] = useState(false);
 * <ExpandableSearch
 *   value={search}
 *   onChange={setSearch}
 *   isExpanded={expanded}
 *   onExpandedChange={setExpanded}
 * />
 */
export function ExpandableSearch({
  isExpanded = false,
  onExpandedChange,
  ...props
}: ExpandableSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  const handleExpand = () => {
    onExpandedChange?.(true);
  };

  const handleEscape = () => {
    if (!props.value) {
      onExpandedChange?.(false);
    }
    props.onEscape?.();
  };

  if (!isExpanded) {
    return (
      <Button variant="outline" size="icon" onClick={handleExpand}>
        <Search className="h-4 w-4" />
        <span className="sr-only">Search</span>
      </Button>
    );
  }

  return (
    <SearchInput
      ref={inputRef}
      {...props}
      autoFocus
      onEscape={handleEscape}
    />
  );
}

export default SearchInput;
